import { useState } from "react"
import { Box, Button, Stack, Typography, useTheme } from "@mui/material"
import { formatarBRL } from "@/lib/formato"
import NumericKeypad from "../components/numeric-keypad"

const FRACOES = [2, 3, 4] as const

const MAX_DIGITOS = 7

interface ValorInputProps {
    saldoCentavos: number
    aoConfirmar: (centavos: number) => void
}

export default function ValorInput({ saldoCentavos, aoConfirmar }: ValorInputProps): JSX.Element {
    const theme = useTheme()
    const [digitos, setDigitos] = useState('')

    const centavos = Number(digitos || 0)
    const excedeSaldo = centavos > saldoCentavos
    const podeConfirmar = centavos > 0 && !excedeSaldo

    const aoDigitar = (digito: string): void => {
        setDigitos((atual) => (atual + digito).replace(/^0+/, '').slice(0, MAX_DIGITOS))
    }

    const aoApagar = (): void => setDigitos((atual) => atual.slice(0, -1))

    const confirmar = (): void => {
        if (podeConfirmar) aoConfirmar(centavos)
    }

    return (
        <Stack spacing={2} sx={{ alignItems: 'center', width: '100%', maxWidth: 520 }}>
            <Stack spacing={2} sx={{ alignItems: 'center' }}>
                <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
                    Quanto você vai pagar?
                </Typography>

                <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 400 }}>
                    Falta pagar {formatarBRL(saldoCentavos / 100)} nesta conta
                </Typography>
            </Stack>

            <Box
                sx={{
                    width: '100%',
                    minHeight: 96,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px',
                    border: '2px solid',
                    borderColor: excedeSaldo ? 'error.main' : 'transparent',
                    backgroundColor: theme.palette.personal.header,
                }}
            >
                <Typography
                    variant="h2"
                    sx={{ fontWeight: 900, color: excedeSaldo ? 'error.main' : 'success.main' }}
                >
                    {formatarBRL(centavos / 100)}
                </Typography>
            </Box>

            {excedeSaldo && (
                <Typography
                    variant="body1"
                    color="error.main"
                    sx={{ fontWeight: 600, minHeight: 24, visibility: excedeSaldo ? 'visible' : 'hidden' }}
                >
                    O máximo desta conta é {formatarBRL(saldoCentavos / 100)}
                </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                <BotaoAtalho
                    rotulo="Tudo"
                    valorCentavos={saldoCentavos}
                    aoTocar={() => setDigitos(String(saldoCentavos))}
                />

                {FRACOES.map((fracao) => {
                    const valor = Math.round(saldoCentavos / fracao)

                    return (
                        <BotaoAtalho
                            key={fracao}
                            rotulo={`1/${fracao}`}
                            valorCentavos={valor}
                            desabilitado={valor <= 0}
                            aoTocar={() => setDigitos(String(valor))}
                        />
                    )
                })}
            </Stack>

            <Box sx={{ width: '100%', height: 340 }}>
                <NumericKeypad
                    onDigito={aoDigitar}
                    onApagar={aoApagar}
                    onConfirmar={confirmar}
                    apagarDesabilitado={digitos === ''}
                    confirmarDesabilitado={!podeConfirmar}
                />
            </Box>
        </Stack>
    )
}

function BotaoAtalho({ rotulo, valorCentavos, desabilitado = false, aoTocar }: {
    rotulo: string
    valorCentavos: number
    desabilitado?: boolean
    aoTocar: () => void
}): JSX.Element {
    return (
        <Button
            variant="outlined"
            color="inherit"
            onClick={aoTocar}
            disabled={desabilitado}
            sx={{ flex: 1, py: 1, borderRadius: 2, textTransform: 'none', borderColor: 'divider' }}
        >
            <Stack sx={{ alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.15rem' }}>
                    {rotulo}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                    {formatarBRL(valorCentavos / 100)}
                </Typography>
            </Stack>
        </Button>
    )
}
